import { FormikProps } from "formik";
import { getTexts } from "../texts";
import { AccordionBody, AccordionHeader, AccordionItem } from "../../Accordion";
import { Heading4, Heading5 } from "../Headings";
import { FormRadio, FormRadioList } from "../../form/FormRadio";
import ValidationError from "../../form/ValidationError";
import { FormCheck } from "../../form/FormCheck";
import { TilsynserklaeringFormValues } from "../../../interfaces/tilsynserklaering";
import { isOneChecked } from "../../../util/common";
import { tilsynSection4CompleteHelper } from "../../../util/tilsynserklaering/isComplete";
import IsRequired from "../../form/IsRequired";
import ValidatedField from "../../form/ValidatedField";
import { Fragment } from "react/jsx-runtime";

const TilsynerklaeringSection4 = ({
  texts,
  props,
}: {
  texts: ReturnType<typeof getTexts>;
  props: FormikProps<TilsynserklaeringFormValues>;
}) => {
  const { values, errors, touched } = props;
  const { section4IsStarted, section4IsComplete } =
    tilsynSection4CompleteHelper(values, errors, touched);
  return (
    <AccordionItem value="4">
      <AccordionHeader
        sectionStarted={section4IsStarted}
        sectionComplete={section4IsComplete}
      >
        {texts.section4.title}
      </AccordionHeader>
      <AccordionBody>
        <div className="vstack gap-5">
          <div>
            <Heading4>{texts.section4.section4_1.title}</Heading4>
            <Heading5>
              {texts.section4.section4_1.question.label}
              <IsRequired />
            </Heading5>
            <FormRadioList>
              {texts.section4.section4_1.question.options.map((option) => (
                <FormRadio
                  key={option.value}
                  name={texts.section4.section4_1.question.id}
                  value={option.value}
                >
                  {option.label}
                </FormRadio>
              ))}
              <ValidationError
                error={errors.harDuHaftHvidvasksager}
                show={touched.harDuHaftHvidvasksager}
              />
            </FormRadioList>
          </div>
          {values.harDuHaftHvidvasksager === "ja" && (
            <>
              <p className="mb-0 fw-semibold">
                {texts.section4.section4_2.description}
              </p>
              <div>
                <Heading4>{texts.section4.section4_2.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_2.question.label} <IsRequired />
                </Heading5>

                <div className="d-flex flex-column gap-2 mb-5">
                  {texts.section4.section4_2.question.options.map((option) => (
                    <Fragment key={option.value}>
                      <FormCheck
                        id={option.value}
                        isInvalid={isOneChecked("sagstype-", touched, values)}
                      >
                        {option.label}
                      </FormCheck>

                      {values[option.value] && (
                        <div className="mb-3 ps-5 ms-1">
                          <Heading5 className="mt-2 mb-3 fw-light">
                            {option.inputQuestion.label} <IsRequired />
                          </Heading5>
                          <ValidatedField
                            error={props.errors?.[option.inputQuestion.id]}
                            touched={touched?.[option.inputQuestion.id]}
                            id={option.inputQuestion.id}
                            name={option.inputQuestion.id}
                            type="number"
                            min={1}
                            autoComplete="off"
                            additionalClasses="w-25"
                            placeholder="Angiv antal sager"
                            wrapper
                          />
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>
              </div>
              <div>
                <Heading4>{texts.section4.section4_3.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_3.question.label}
                  <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_3.question.options.map((option) => (
                    <FormRadio
                      key={option.value}
                      name={texts.section4.section4_3.question.id}
                      value={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_4.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_4.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_4.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_4.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_5.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_5.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_5.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_5.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_6.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_6.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_6.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_6.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_7.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_7.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_7.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_7.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_8.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_8.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_8.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_8.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_9.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_9.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_9.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_9.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_10.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_10.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_10.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_10.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_11.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_11.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_11.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_11.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_12.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_12.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_12.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_12.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_13.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_13.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_13.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_13.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              <div>
                <Heading4>{texts.section4.section4_14.title}</Heading4>
                <Heading5 className="mb-2">
                  {texts.section4.section4_14.question.label} <IsRequired />
                </Heading5>
                <FormRadioList>
                  {texts.section4.section4_14.question.options.map((option) => (
                    <FormRadio
                      name={texts.section4.section4_14.question.id}
                      value={option.value}
                      key={option.value}
                    >
                      {option.label}
                    </FormRadio>
                  ))}
                </FormRadioList>
              </div>
              {values.virksomhedRisikovurderingAdvokatvirksomhed !==
                "ikkeBeskaeftigetAdvokatvirksomhed" && (
                <>
                  {values.virksomhedRisikovurderingAdvokatvirksomhed ===
                    "ja" && (
                    <div>
                      <Heading4>{texts.section4.section4_14a.title}</Heading4>
                      <Heading5 className="mb-2">
                        {texts.section4.section4_14a.question.label}{" "}
                        <IsRequired />
                      </Heading5>
                      <FormRadioList>
                        {texts.section4.section4_14a.question.options.map(
                          (option) => (
                            <FormRadio
                              name={texts.section4.section4_14a.question.id}
                              value={option.value}
                              key={option.value}
                            >
                              {option.label}
                            </FormRadio>
                          ),
                        )}
                      </FormRadioList>
                    </div>
                  )}

                  <div>
                    <Heading4>{texts.section4.section4_15.title}</Heading4>
                    <Heading5 className="mb-2">
                      {texts.section4.section4_15.question.label} <IsRequired />
                    </Heading5>
                    <FormRadioList>
                      {texts.section4.section4_15.question.options.map(
                        (option) => (
                          <FormRadio
                            name={texts.section4.section4_15.question.id}
                            value={option.value}
                            key={option.value}
                          >
                            {option.label}
                          </FormRadio>
                        ),
                      )}
                    </FormRadioList>
                  </div>
                  {values.advokatvirksomhedPolitikHvidvaskloven === "ja" && (
                    <div>
                      <Heading4>{texts.section4.section4_15a.title}</Heading4>
                      <Heading5 className="mb-2">
                        {texts.section4.section4_15a.question.label}{" "}
                        <IsRequired />
                      </Heading5>
                      <FormRadioList>
                        {texts.section4.section4_15a.question.options.map(
                          (option) => (
                            <FormRadio
                              name={texts.section4.section4_15a.question.id}
                              value={option.value}
                              key={option.value}
                            >
                              {option.label}
                            </FormRadio>
                          ),
                        )}
                      </FormRadioList>
                    </div>
                  )}

                  <div>
                    <Heading4>{texts.section4.section4_16.title}</Heading4>
                    <Heading5 className="mb-2">
                      {texts.section4.section4_16.question.label} <IsRequired />
                    </Heading5>
                    <FormRadioList>
                      {texts.section4.section4_16.question.options.map(
                        (option) => (
                          <FormRadio
                            name={texts.section4.section4_16.question.id}
                            value={option.value}
                            key={option.value}
                          >
                            {option.label}
                          </FormRadio>
                        ),
                      )}
                    </FormRadioList>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </AccordionBody>
    </AccordionItem>
  );
};

export default TilsynerklaeringSection4;
